/**
 * Transactions hooks for React Query
 */
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  transactionsService,
  type TransactionsListParams,
  type TransactionUpdatePayload,
  type SpendingByCategoryParams,
  type SpendingByCategory,
  type TransactionsListResponse,
  type TransactionWithSplits,
  type BulkCategorizeResponse,
} from '@/services/transactions';
import type { Transaction, TransactionFilters } from '@/types';

const TRANSACTIONS_KEY = 'transactions';
const SPENDING_KEY = 'spending-by-category';

/**
 * Hook for fetching paginated transactions with infinite scroll
 * Returns UseInfiniteQueryResult where data.pages is TransactionsListResponse[]
 */
export function useTransactions(filters: TransactionFilters) {
  return useInfiniteQuery({
    queryKey: [TRANSACTIONS_KEY, filters],
    queryFn: async ({ pageParam }): Promise<TransactionsListResponse> => {
      const params: TransactionsListParams = {
        ...filters,
        page: pageParam,
        pageSize: 50,
      };
      return transactionsService.getAll(params);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasMore) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pagination.page > 1) {
        return firstPage.pagination.page - 1;
      }
      return undefined;
    },
  });
}

/**
 * Hook for fetching a single transaction
 */
export function useTransaction(id: string | undefined) {
  return useQuery({
    queryKey: [TRANSACTIONS_KEY, id],
    queryFn: () => transactionsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook for updating a transaction
 */
export function useUpdateTransaction(): UseMutationResult<
  Transaction,
  Error,
  { id: string; payload: TransactionUpdatePayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => transactionsService.update(id, payload),
    onSuccess: (updatedTransaction) => {
      // Update the single transaction cache
      queryClient.setQueryData(
        [TRANSACTIONS_KEY, updatedTransaction.id],
        updatedTransaction
      );
      // Invalidate the list queries to refetch
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] });
      // Also invalidate spending data since category might have changed
      queryClient.invalidateQueries({ queryKey: [SPENDING_KEY] });
    },
  });
}

/**
 * Hook for bulk categorizing transactions
 */
export function useBulkUpdateTransactions(): UseMutationResult<
  BulkCategorizeResponse,
  Error,
  { transactionIds: string[]; payload: TransactionUpdatePayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionIds, payload }) =>
      transactionsService.bulkCategorize({
        transactionIds,
        categoryId: payload.categoryId || '',
      }),
    // onSettled, not onSuccess: a chunked update can fail part-way after some
    // chunks were saved (BulkUpdatePartialError), and the list must show them.
    onSettled: () => {
      // Invalidate all transaction queries
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] });
      // Also invalidate spending data
      queryClient.invalidateQueries({ queryKey: [SPENDING_KEY] });
    },
  });
}

/**
 * Hook for fetching spending by category
 */
export function useSpendingByCategory(params: SpendingByCategoryParams) {
  return useQuery<SpendingByCategory[], Error>({
    queryKey: [SPENDING_KEY, params],
    queryFn: () => transactionsService.getSpendingByCategory(params),
  });
}

/**
 * Flatten paginated transactions from infinite query
 */
export function flattenTransactions(
  data: InfiniteData<TransactionsListResponse> | undefined
): TransactionWithSplits[] {
  if (!data?.pages) return [];
  return data.pages.flatMap((page) => page.transactions);
}

/**
 * Hook for deleting a transaction
 * Follows pattern from useDeleteConnection in useConnections.ts
 */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) => transactionsService.delete(transactionId),
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: [TRANSACTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SPENDING_KEY] });
      queryClient.invalidateQueries({ queryKey: ['flows'] });
    },
  });
}

export default useTransactions;
